import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import ShotList from './ShotList';
import { GetSetupHeader } from '../isdlData/sessionData';
import { getSetupColor } from '../utils/LMConstants';
//import Options from './Options';

function ShotListsPanel({
  sessionSetups = [],
  activeSetupId = null,
  selectedShot = null,
  selectedShotSetupId = null,
  onShotSelected,
  handleShotChange,
  replacingShot,
  setReplacingShot,
  showFavoritesOnly,
  onFavoritesToggle,
  justAvSt,
  ShowAllSetups = false,
  ShowDetailedSummary,
  onShotsChanged,
  onManualShotCheck
  

}) {
  const showAllSetups = !!(ShowAllSetups ?? false); // null/undefined/false → false; true → true
  //const scrollRef = useRef(null);
  const panelClassName = `shotlist-panel-root${showAllSetups ? ' shotlist-panel-root--summary' : ''}`;
  const unifiedScrollbarRef = useRef(null);
  const viewportRegistryRef = useRef(new Map());
  const syncSourceRef = useRef(null);
  const [unifiedScrollMetrics, setUnifiedScrollMetrics] = useState({ clientWidth: 0, scrollWidth: 0 });

  const orderedSetups = useMemo(() => {
  let validSetups = (sessionSetups || []).filter(setup => setup && setup.IsChecked);
  if(showAllSetups) { 
    validSetups = sessionSetups || [];
  }

    if (!activeSetupId) {
      return validSetups;
    }

    const activeIndex = validSetups.findIndex(setup => setup.Id === activeSetupId);
    if (activeIndex <= 0) {
      return validSetups;
    }

    const reordered = [...validSetups];
    const [activeSetup] = reordered.splice(activeIndex, 1);
    reordered.unshift(activeSetup);
    return reordered;
  
  }, [sessionSetups, activeSetupId, showAllSetups]);

  const refreshUnifiedScrollMetrics = useCallback(() => {
    const viewports = Array.from(viewportRegistryRef.current.values()).filter(Boolean);
    if (!viewports.length) {
      setUnifiedScrollMetrics({ clientWidth: 0, scrollWidth: 0 });
      return;
    }

    const nextMetrics = viewports.reduce((acc, viewport) => ({
      clientWidth: Math.max(acc.clientWidth, viewport.clientWidth || 0),
      scrollWidth: Math.max(acc.scrollWidth, viewport.scrollWidth || 0)
    }), { clientWidth: 0, scrollWidth: 0 });

    setUnifiedScrollMetrics((previous) => {
      if (
        previous.clientWidth === nextMetrics.clientWidth &&
        previous.scrollWidth === nextMetrics.scrollWidth
      ) {
        return previous;
      }

      return nextMetrics;
    });
  }, []);

  const syncUnifiedScrollbarTo = useCallback((scrollLeft) => {
    const scrollbar = unifiedScrollbarRef.current;
    if (!scrollbar) {
      return;
    }

    if (Math.abs((scrollbar.scrollLeft || 0) - scrollLeft) > 1) {
      syncSourceRef.current = 'viewport';
      scrollbar.scrollLeft = scrollLeft;
      window.requestAnimationFrame(() => {
        if (syncSourceRef.current === 'viewport') {
          syncSourceRef.current = null;
        }
      });
    }
  }, []);

  const registerHorizontalViewport = useCallback((setupId, viewport) => {
    if (!setupId) {
      return;
    }

    const registry = viewportRegistryRef.current;
    const existingViewport = registry.get(setupId);

    if (existingViewport?.__summaryScrollListener) {
      existingViewport.removeEventListener('scroll', existingViewport.__summaryScrollListener);
      delete existingViewport.__summaryScrollListener;
    }

    if (!viewport) {
      registry.delete(setupId);
      refreshUnifiedScrollMetrics();
      return;
    }

    const handleViewportScroll = () => {
      if (syncSourceRef.current === 'unified-scrollbar') {
        return;
      }

      syncUnifiedScrollbarTo(viewport.scrollLeft || 0);
      refreshUnifiedScrollMetrics();
    };

    viewport.__summaryScrollListener = handleViewportScroll;
    viewport.addEventListener('scroll', handleViewportScroll, { passive: true });
    registry.set(setupId, viewport);
    syncUnifiedScrollbarTo(viewport.scrollLeft || 0);
    refreshUnifiedScrollMetrics();
  }, [refreshUnifiedScrollMetrics, syncUnifiedScrollbarTo]);

  useEffect(() => {
    if (!showAllSetups) {
      return undefined;
    }

    refreshUnifiedScrollMetrics();

    const handleResize = () => {
      refreshUnifiedScrollMetrics();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [showAllSetups, orderedSetups.length, refreshUnifiedScrollMetrics]);

  useEffect(() => {
    if (!showAllSetups) {
      return undefined;
    }

    const scrollbar = unifiedScrollbarRef.current;
    if (!scrollbar) {
      return undefined;
    }

    const handleUnifiedScroll = () => {
      if (syncSourceRef.current === 'viewport') {
        syncSourceRef.current = null;
        return;
      }

      syncSourceRef.current = 'unified-scrollbar';
      const nextScrollLeft = scrollbar.scrollLeft || 0;
      viewportRegistryRef.current.forEach((viewport) => {
        if (viewport && Math.abs((viewport.scrollLeft || 0) - nextScrollLeft) > 1) {
          viewport.scrollLeft = nextScrollLeft;
        }
      });

      window.requestAnimationFrame(() => {
        if (syncSourceRef.current === 'unified-scrollbar') {
          syncSourceRef.current = null;
        }
      });
    };

    scrollbar.addEventListener('scroll', handleUnifiedScroll, { passive: true });
    return () => {
      scrollbar.removeEventListener('scroll', handleUnifiedScroll);
    };
  }, [showAllSetups, unifiedScrollMetrics.clientWidth, unifiedScrollMetrics.scrollWidth]);

  useEffect(() => () => {
    viewportRegistryRef.current.forEach((viewport) => {
      if (viewport?.__summaryScrollListener) {
        viewport.removeEventListener('scroll', viewport.__summaryScrollListener);
        delete viewport.__summaryScrollListener;
      }
    });
    viewportRegistryRef.current.clear();
  }, []);
  if (!orderedSetups.length) {
    return (
      <div style={{ padding: '16px', textAlign: 'center' }}>
        No setups selected.
      </div>
    );
  }

  return (
     <div className={panelClassName}>      <div className={`shotlist-panel-content${showAllSetups ? ' shotlist-panel-content--summary' : ''}`}>
        {orderedSetups.map((setup) => {
          const headerTitle = GetSetupHeader ? GetSetupHeader(setup) : (setup.DisplayName || setup.Id);
          const headerBadge = setup.LetterId || '';
          const isActive = activeSetupId === setup.Id;
          const highlightedShot =
            selectedShot && selectedShotSetupId === setup.Id ? selectedShot : null;

          const originalIndex = (sessionSetups || []).findIndex(
            original => original && original.Id === setup.Id
          );
          const colorToken = getSetupColor(setup, Math.max(originalIndex, 0));

          return (
            <div key={setup.Id} className={`shotlist-panel-section ${isActive ? 'active' : 'inactive'}`}>
              <div
                className='shotlist-panel-header'
                style={{ backgroundColor: colorToken.bg, color: colorToken.fg }}
              >
                <div className='shotlist-panel-badge'>{headerBadge}</div>
                <div className='shotlist-panel-title'>
                  <h3>
                    {headerTitle}
                    {!isActive && <span className='shotlist-panel-subtitle'> (inactive)</span>}
                  </h3>
                </div>
                {setup.Shots && (
                  <div className='shotlist-panel-meta'>
                    {setup.Shots.length} shot{setup.Shots.length === 1 ? '' : 's'}
                  </div>
                )}
              </div>
              <div className='shotlist-panel-body'>
                <ShotList
                  currentSetup={setup}
                  setShot={(shot) => onShotSelected(shot, setup.Id)}
                  inSelectedShot={highlightedShot}
                  handleShotChange={handleShotChange}
                  replacingShot={replacingShot}
                  setReplacingShot={setReplacingShot}
                  showFavoritesOnly={showFavoritesOnly}
                  onFavoritesToggle={onFavoritesToggle}
                  displayFavoritesControls={false}
                  justAvSt={justAvSt}
                  ShowAllSetups={showAllSetups}
                  hideHeaderCheckbox={showAllSetups}
                  ShowDetailedSummary={ShowDetailedSummary}
                  onShotsChanged={onShotsChanged}
                  onManualShotCheck={onManualShotCheck}
                  onHorizontalViewportReady={showAllSetups ? registerHorizontalViewport : undefined}
                />
              </div>
            </div>
          );
        })}
      </div>
      {showAllSetups && unifiedScrollMetrics.scrollWidth > unifiedScrollMetrics.clientWidth && (
        <div className='shotlist-panel-unified-scrollbar-shell'>
          <div ref={unifiedScrollbarRef} className='shotlist-panel-unified-scrollbar'>
            <div
              className='shotlist-panel-unified-scrollbar-width'
              style={{ width: `${unifiedScrollMetrics.scrollWidth}px` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ShotListsPanel; 

